/* eslint-disable array-callback-return */
import React from "react";
import {
  AppBar,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
} from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import * as XLSX from "xlsx";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import moment from "moment";
import { useSelector } from "react-redux";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import API from "../../Api/api";
import toast from "react-hot-toast";
import { endOfMonth, startOfMonth } from "../../ui/today";

function createData(
  id,
  branch,
  category,
  designation,
  department,
  description,
  employee_name,
  date,
  amount,
  sanctioned_by,
  approved_by
) {
  return {
    id,
    branch,
    category,
    designation,
    department,
    description,
    employee_name,
    date,
    amount,
    sanctioned_by,
    approved_by,
  };
}
const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  maximumFractionDigits: 2,
});
export default function ClaimSummaryReport() {
  const [showingData, setShowingData] = React.useState("data");
  const departments = useSelector((state) => state.departments.depart);
  const category = useSelector((state) => state.category.cart);
  const locations = useSelector((state) => state.locations);
  const approvedClaims = useSelector((state) => state.approved);
  const [approved, setApproved] = React.useState(approvedClaims);
  const [queryData, setQueryData] = React.useState({
    branch: "",
    category: "",
    department: "",
    claim_start: "",
    claim_end: "",
    designation: "",
  });

  const branch = queryData.branch
    ? `&branch[eq]=${queryData.branch.toLowerCase()}`
    : "";
  const categoryQuery = queryData.category
    ? `&category[eq]=${queryData.category}`
    : "";
  const departmentQuery = queryData.department
    ? `&department[eq]=${queryData.department}`
    : "";
  const claimStartQuery = queryData.claim_start
    ? `&date[gte]=${queryData.claim_start}`
    : "";
  const claimEndQuery = queryData.claim_end
    ? `&date[lte]=${queryData.claim_end}`
    : "";
  const designationQuery = queryData.designation
    ? `&designation[eq]=${queryData.designation}`
    : "";
  const query = `/api/v1/eClaim?status[eq]=approved${branch}${categoryQuery}${departmentQuery}${claimStartQuery}${designationQuery}${claimEndQuery}`;
  const fetchData = async () => {
    let fetchingId;
    try {
      fetchingId = toast.loading("Fetching data...");
      const res = await API.get(query);
      if (res.data) {
        setApproved(res.data);
      }
      toast.remove(fetchingId);
      toast.success("Data Loaded");
    } catch (error) {
      toast.remove(fetchingId);
      toast.error(error.message);
      setApproved(approvedClaims);
    }
  };
  React.useEffect(() => {
    if (showingData === "month") {
      const data = approvedClaims.filter(
        (d) =>
          moment(d.date) >= moment(startOfMonth) &&
          moment(d.date) < moment(endOfMonth)
      );
      setApproved(data);
    } else {
      setApproved(approvedClaims);
    }
  }, [showingData, approvedClaims]);
  function clearFilter() {
    if (showingData === "month") {
      const data = approvedClaims.filter(
        (d) =>
          moment(d.date) >= moment(startOfMonth) &&
          moment(d.date) < moment(endOfMonth)
      );
      setApproved(data);
      setQueryData({
        branch: "",
        category: "",
        department: "",
        claim_start: "",
        claim_end: "",
        designation: "",
      });
      return;
    }
    setQueryData({
      branch: "",
      category: "",
      department: "",
      claim_start: "",
      claim_end: "",
      designation: "",
    });
    setApproved(approvedClaims);
  }
  const rows = approved
    .filter((d) => d.status === "approved")
    .map((data) =>
      createData(
        data.claim_code,
        data.branch,
        data.category,
        data.designation || "staff",
        data.department,
        data.description,
        data.employee_name,
        moment(data.date).format("DD-MM-YYYY"),
        data.amount,
        data.sanctioned_by,
        data.approved_by
      )
    );

  const groupedData = rows.reduce((result, item) => {
    const { department, id, amount } = item;
    if (!result[department]) {
      result[department] = {
        department,
        amount: 0,
      };
    }
    result[department].id = id;
    result[department].amount += amount;

    return result;
  }, {});

  // Converting the grouped data object back to an array (if needed)
  const groupedArray = Object.values(groupedData);
  const name = [];
  const value = [];
  groupedArray.map((data) => {
    name.push(data.department);
    value.push(data.amount);
  });

  const groupedData1 = rows.reduce((acc, item) => {
    const department = item.department;
    const category = item.category;
    const amount = item.amount;

    // Check if the department already exists in the result
    const departmentGroup = acc.find(
      (group) => group.department === department
    );

    if (departmentGroup) {
      // Check if the category exists in the department's categories
      const existingCategory = departmentGroup.categories.find(
        (cat) => cat.category === category
      );

      if (existingCategory) {
        // If the category exists, add the amount to the total amount
        existingCategory.amount += amount;
      } else {
        // If the category does not exist, add it with the amount
        departmentGroup.categories.push({ category, amount });
      }
    } else {
      // If the department doesn't exist in the result, create a new entry
      acc.push({
        department: department,
        categories: [{ category, amount }],
      });
    }

    return acc;
  }, []);

  const allCategories = [...new Set(rows.map((item) => item.category))];

  // Function to handle export to Excel
  const handleExportToExcel = () => {
    // Create an array of data that matches your table structure
    const exportData = groupedData1.map((departmentData) => {
      const row = {
        Department: departmentData.department,
      };

      allCategories.forEach((category) => {
        const foundCategory = departmentData.categories.find(
          (cat) => cat.category === category
        );
        row[category] = foundCategory ? foundCategory.amount : 0;
      });

      row["Total"] = departmentData.categories.reduce(
        (sum, cat) => sum + (cat.amount || 0),
        0
      );

      return row;
    });

    // Convert JSON data to worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Create a new workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Departments");

    // Export the workbook to an Excel file
    XLSX.writeFile(workbook, "departments_report.xlsx");
  };

  return (
    <Paper elevation={0} style={{ paddingBottom: "50px" }}>
      <Container>
        <AppBar
          position='fixed'
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "10px",
            flexGrow: 1,
            marginTop: "60px",
            width: "95%",
            backgroundColor: "#ccc",
            color: "#000",
          }}>
          <div
            elevation={16}
            style={{
              fontSize: "14px",
              // marginBottom: "10px",
              display: "flex",
              flexDirection: "row",
              // alignItems: "center",
              justifyContent: "space-between",
            }}>
            <div
              style={{ display: "flex", flexDirection: "column", rowGap: 9 }}>
              <FormControl size='small' sx={{ m: 1, width: 140 }}>
                <InputLabel id='demo-simple-select-standard-label'>
                  Branch
                </InputLabel>
                <Select
                  labelId='demo-simple-select-standard-label'
                  id='demo-simple-select-standard'
                  value={queryData.branch}
                  onChange={(e) =>
                    setQueryData({ ...queryData, branch: e.target.value })
                  }
                  label='Category'>
                  <MenuItem value=''>
                    <em>None</em>
                  </MenuItem>
                  {locations?.map((data) => {
                    return (
                      <MenuItem key={data._id} value={data.name}>
                        {data.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", rowGap: 9 }}>
              <FormControl size='small' sx={{ m: 1, width: 140 }}>
                <InputLabel id='demo-simple-select-standard-label'>
                  Department
                </InputLabel>
                <Select
                  labelId='demo-simple-select-standard-label'
                  id='demo-simple-select-standard'
                  value={queryData.department}
                  onChange={(e) =>
                    setQueryData({ ...queryData, department: e.target.value })
                  }
                  label='Category'>
                  <MenuItem value=''>
                    <em>None</em>
                  </MenuItem>
                  {departments.map((data) => (
                    <MenuItem key={data._id} value={data.department}>
                      {data.department}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <FormControl size='small' sx={{ m: 1, width: 140 }}>
                <InputLabel id='demo-simple-select-standard-label'>
                  Category
                </InputLabel>
                <Select
                  labelId='demo-simple-select-standard-label'
                  id='demo-simple-select-standard'
                  value={queryData.category}
                  onChange={(e) =>
                    setQueryData({ ...queryData, category: e.target.value })
                  }
                  label='Category'>
                  <MenuItem value=''>
                    <em>None</em>
                  </MenuItem>
                  {category.map((data) => (
                    <MenuItem key={data._id} value={data.category}>
                      {data.category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <FormControl size='small' sx={{ m: 1, width: 140 }}>
                <InputLabel id='demo-simple-select-standard-label'>
                  Designation
                </InputLabel>
                <Select
                  labelId='demo-simple-select-standard-label'
                  id='demo-simple-select-standard'
                  value={queryData.designation}
                  onChange={(e) =>
                    setQueryData({ ...queryData, designation: e.target.value })
                  }
                  label='Designation'>
                  <MenuItem value=''>
                    <em>None</em>
                  </MenuItem>

                  <MenuItem value='staff'>staff</MenuItem>
                  <MenuItem value='non-staff'>non-staff</MenuItem>
                </Select>
              </FormControl>
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <label
                style={{
                  fontWeight: "bold",
                  fontSize: "14px",
                  color: "#116149",
                }}
                htmlFor='acq-start-date'>
                Claim start date
              </label>
              <input
                value={queryData.claim_start}
                onChange={(e) =>
                  setQueryData({ ...queryData, claim_start: e.target.value })
                }
                style={{
                  // alignItems: "center",
                  alignText: "center",

                  fontSize: "16px",

                  borderRadius: "5px",
                  // backgroundColor: "#cccc",
                  border: "1px solid #3D4F58",
                }}
                id='acq-end-date'
                type='date'
                size='small'
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                marginLeft: "5px",
              }}>
              <label
                style={{
                  fontWeight: "bold",
                  fontSize: "14px",
                  color: "#116149",
                }}
                htmlFor='acq-start-date'>
                Claim end date
              </label>
              <input
                value={queryData.claim_end}
                onChange={(e) =>
                  setQueryData({ ...queryData, claim_end: e.target.value })
                }
                style={{
                  // alignItems: "center",
                  alignText: "center",

                  fontSize: "16px",

                  borderRadius: "5px",
                  // backgroundColor: "#cccc",
                  border: "1px solid #3D4F58",
                }}
                id='acq-end-date'
                type='date'
                size='small'
              />
            </div>
          </div>
          <div
            style={{
              // marginTop: "-35px",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              columnGap: "10px",
            }}>
            <div>
              <FormControl
                size='small'
                disabled={
                  queryData.branch ||
                  queryData.category ||
                  queryData.designation ||
                  queryData.claim_end ||
                  queryData.claim_start
                }>
                <FormLabel
                  style={{ fontSize: "12px" }}
                  id='demo-row-radio-buttons-group-label'>
                  Show Data For
                </FormLabel>
                <RadioGroup
                  value={showingData}
                  onChange={(e) => setShowingData(e.target.value)}
                  row
                  aria-labelledby='demo-row-radio-buttons-group-label'
                  name='row-radio-buttons-group'>
                  <FormControlLabel
                    value='month'
                    control={<Radio size='small' />}
                    label='This Month'
                  />
                  <FormControlLabel
                    value='data'
                    control={<Radio size='small' />}
                    label='All Data'
                  />
                </RadioGroup>
              </FormControl>
            </div>
            <Button
              onClick={fetchData}
              size='small'
              variant='contained'
              style={{ backgroundColor: "#75CA93" }}>
              Generate Report
            </Button>
            <Button
              onClick={clearFilter}
              size='small'
              variant='contained'
              style={{ backgroundColor: "#f47373" }}>
              Clear Filter
            </Button>
          </div>
        </AppBar>
      </Container>

      <TableContainer
        component={Paper}
        elevation={0}
        style={{
          marginTop: "10px",
          overflow: "auto",
        }}>
        <Button onClick={handleExportToExcel} size='small'>
          Export Data <FileUploadIcon />
        </Button>
        <Table
          size='small'
          sx={{
            minWidth: 750,
            width: "3000px",
            maxWidth: "4000px",
            overflow: "auto",
            "& tr > *:nth-child(1)": {
              left: 0,
              position: "sticky",
              bgcolor: "#fff",
              fontWeight: "bold",
            },
            "& tr > *:last-child": {
              right: 0,
              position: "sticky",
              bgcolor: "#fff",
              fontWeight: "bold",
            },
          }}
          aria-label='simple table'>
          <TableHead>
            <TableRow
              style={{ backgroundColor: "#ccc", textTransform: "lowercase" }}>
              <TableCell style={{ backgroundColor: "#ccc" }}>
                Department
              </TableCell>
              {allCategories.map((category) => (
                <TableCell
                  style={{ textTransform: "lowercase", fontWeight: "bold" }}
                  key={category}>
                  {category}
                </TableCell>
              ))}
              <TableCell style={{ backgroundColor: "#ccc" }}>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedData1.map((departmentData) => {
              // Calculate total for each department
              const total = departmentData.categories.reduce(
                (sum, cat) => sum + (cat.amount || 0),
                0
              );

              return (
                <TableRow key={departmentData.department}>
                  <TableCell>{departmentData.department}</TableCell>
                  {allCategories.map((category) => (
                    <TableCell key={category}>
                      {departmentData.categories.find(
                        (cat) => cat.category === category
                      )
                        ? currencyFormatter.format(
                            departmentData.categories.find(
                              (cat) => cat.category === category
                            ).amount
                          )
                        : 0}
                    </TableCell>
                  ))}
                  <TableCell>{currencyFormatter.format(total)}</TableCell>{" "}
                  {/* Display the total */}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
